import CustomTooltip from '../../shared/CustomTooltip';
import { useState, useRef } from 'react';
import {
    KnockFeedProvider,
    NotificationIconButton,
    NotificationFeedPopover,
    OnNotificationClick,
} from '@knocklabs/react-notification-feed';
import '@knocklabs/react-notification-feed/dist/index.css';

import {
    Box,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    useMediaQuery,
} from '@mui/material';
import { createSearchParams, useNavigate } from 'react-router-dom';
import UserRole from '../../../shared/AuthRoles';
import { theme } from '../../../shared/theme';
import React from 'react';
import { trackEvent } from '../../../app/Analytics';

type Props = {
    compact?: boolean;
    canMini?: boolean;
    variant?: 'drawer' | 'topbar';
    rowPx?: number;
};

export default function NotificationsCenter({
    compact = false,
    canMini = false,
    variant = 'drawer',
    rowPx = 0,
}: Props) {
    const loggedInUser = UserRole();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width: 1200px)');
    const [isVisible, setIsVisible] = useState(false);
    const notifButtonRef = useRef<any>(null);

    const onNotificationClick: OnNotificationClick = (item: any) => {
        setIsVisible(false);

        if (item.data.waitListID) {
            navigate(`/reservations`, {
                state: {
                    page: 'waitlist',
                    name: 'viewWaitlist',
                    component: 'notifications',
                },
            });
        }

        if (item.data.bookingId) {
            navigate({
                pathname: `/reservations/experience/${item.data.bookingId}`,
                search: item.data.accessRequests
                    ? createSearchParams({ accessRequests: '1' }).toString()
                    : '',
            });
        }

        if (item.data.inviteId) {
            navigate('/contractorClients');
        }

        if (item.data.jobId) {
            if (loggedInUser?.role.includes('contractor')) {
                navigate({
                    pathname: 'jobs',
                    search: createSearchParams({
                        selectedJob: item.data.jobId,
                    }).toString(),
                });
            } else {
                navigate({
                    pathname: `/reservations/experience/${item.data.bookingId}`,
                    search: createSearchParams({
                        selectedJob: item.data.jobId,
                    }).toString(),
                });
            }
        }
        trackEvent(
            'AppBar',
            'Notification Center',
            `Notification Center Clicked - ${loggedInUser.role}`,
        );
    };

    const toggleNotifications = () => {
        setIsVisible(!isVisible);
        trackEvent(
            'AppBar',
            'Notification Center',
            `Notification Center Clicked - ${loggedInUser.role}`,
        );
    };

    const isMini = canMini && compact;
    const isTopbar = variant === 'topbar';
    const popoverOffset =
        variant === 'drawer' ? (isMobile ? '16px' : '80px') : '12px';

    const dynamicCss = `
:root {
    --rnf-font-family-sanserif: ${theme.typography.fontFamily}
}

.rnf-notification-feed {
    background-color: ${theme.palette.custom.toolbar};
}

.rnf-notification-feed__type {
    color: ${theme.palette.info.main};
}

.rnf-dropdown, .rnf-mark-all-as-read {
    color: ${theme.palette.custom.sidebar.menuItemColor};
}

.rnf-notification-icon-button {
    margin-left: ${isTopbar ? '0px' : isMini ? '-4px' : '0px'};
    margin-right: ${isTopbar ? '0px' : isMini ? '-4px' : '0px'};
}

.rnf-notification-icon-button svg {
    color: ${theme.palette.custom.sidebar.menuItemColor};
    fill: ${theme.palette.custom.sidebar.menuItemColor};
}

.rnf-notification-icon-button svg:hover,
.rnf-notification-icon-button svg:focus {
    color: ${theme.palette.custom.sidebar.iconBackgroundColor};
    fill: ${theme.palette.custom.sidebar.iconBackgroundColor};
}

.rnf-unseen-badge {
    margin-top: -6px !important;
}

.rnf-notification-feed__knock-branding {
    visibility: hidden;
}

.rnf-notification-cell__content {
    color: ${theme.palette.custom.sidebar.menuItemColor};
    font-size: 13px;
    font-weight: 500;
}

.rnf-notification-cell__timestamp {
    color: ${theme.palette.custom.sidebar.menuItemColor};
    font-size: 12px;
}

.rnf-notification-cell:hover {
    background-color: ${theme.palette.custom.sidebar.menuItemHoverBackgroundColor};
}

.rnf-unseen-badge {
    color: ${theme.palette.custom.sidebar.badgeColor};
    background-color: ${theme.palette.custom.sidebar.badgeBackgroundColor};
    border-radius: 10px !important;
    height: 20px;
    min-width: 20px;
    right: -6px;
}

.rnf-unseen-badge__count {
    color: ${theme.palette.custom.sidebar.badgeColor};
    font-size: 0.75rem;
    margin-top: 0;
}

.rnf-notification-cell__unread-dot {
    border-color: ${theme.palette.info.main};
    background: ${theme.palette.info.main};
    margin-top: 10px;
}

.rnf-popover,
.rnf-notification-feed-popover {
    margin-top: ${popoverOffset} !important;
}

${
    isMobile
        ? `
\
.rnf-popover,
.rnf-notification-feed-popover {
    max-width: calc(100vw - 24px) !important;
    z-index: 2000 !important;
    box-sizing: border-box !important;
}

.rnf-notification-feed-popover__inner {
    max-width: 100% !important;
    box-sizing: border-box !important;
}

.rnf-notification-feed {
    width: 100% !important;
    max-width: 100% !important;
    max-height: min(420px, calc(100dvh - 120px)) !important;
    overflow-y: auto !important;
    box-sizing: border-box !important;
    -webkit-overflow-scrolling: touch;
}
`
        : ''
}
`;

    const row = isTopbar ? (
        <NotificationIconButton
            ref={notifButtonRef}
            onClick={toggleNotifications}
        />
    ) : (
        <ListItem
            disablePadding
            sx={{
                px: rowPx,
            }}
        >
            <ListItemButton
                component='div'
                onClick={toggleNotifications}
                sx={{
                    minHeight: 44,
                    justifyContent: isMini ? 'center' : 'flex-start',
                    px: isMini ? 1 : 2,
                    borderRadius: isMini ? 0 : 2,
                    position: 'relative',
                    '&:hover': {
                        backgroundColor:
                            'custom.sidebar.menuItemHoverBackgroundColor',
                    },
                }}
            >
                <ListItemIcon
                    sx={{
                        minWidth: 0,
                        mr: isMini ? 0 : 2,
                        justifyContent: 'center',
                        color: 'custom.sidebar.menuItemColor',
                    }}
                >
                    <Box
                        component='span'
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 24,
                            flexShrink: 0,
                            '& .rnf-notification-icon-button': {
                                margin: 0,
                                padding: 0,
                                lineHeight: 0,
                            },
                        }}
                    >
                        <NotificationIconButton
                            ref={notifButtonRef}
                            onClick={() => undefined}
                        />
                    </Box>
                </ListItemIcon>

                {!isMini && (
                    <ListItemText
                        primary='Notifications'
                        sx={{
                            fontSize: { xs: '14px', md: '16px' },
                        }}
                        primaryTypographyProps={{
                            color: 'custom.sidebar.menuItemColor',
                        }}
                    />
                )}
            </ListItemButton>
        </ListItem>
    );

    return (
        <KnockFeedProvider
            apiKey={process.env.REACT_APP_KNOCK_PUBLIC_API_KEY as string}
            feedId={process.env.REACT_APP_KNOCK_FEED_CHANNEL_ID as string}
            userId={loggedInUser?.sub.toString()}
        >
            <>
                <style>{dynamicCss}</style>

                {isMini ? (
                    <CustomTooltip
                        title='Notifications'
                        placement='right'
                        arrow
                    >
                        <div>{row}</div>
                    </CustomTooltip>
                ) : (
                    row
                )}

                <NotificationFeedPopover
                    buttonRef={notifButtonRef}
                    isVisible={isVisible}
                    onClose={() => setIsVisible(false)}
                    onNotificationClick={onNotificationClick}
                />
            </>
        </KnockFeedProvider>
    );
}
